import React, { useState, useEffect } from 'react';
import {
  FiUsers,
  FiCalendar,
  FiActivity,
  FiPackage,
  FiTrendingUp,
  FiClock,
  FiUserCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';
import styles from './Dashboard.module.css';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalPacientes: 0,
    totalMedicos: 0,
    consultasHoje: 0,
    medicamentos: 0,
    pacientesRecentes: [],
    consultasRecentes: []
  });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados dos pacientes
      const pacientesResponse = await axios.get('/medical/pacientes');
      const totalPacientes = pacientesResponse.data.success ? 
        (pacientesResponse.data.data.pacientes || []).length : 0;

      // Buscar dados dos médicos
      const medicosResponse = await axios.get('/medical/medicos');
      const totalMedicos = medicosResponse.data.success ? 
        (medicosResponse.data.data.medicos || []).length : 0;

      // Buscar dados das consultas
      const consultasResponse = await axios.get('/medical/consultas');
      const consultasHoje = consultasResponse.data.success ? 
        (consultasResponse.data.data.consultas || []).filter(consulta => {
          const hoje = new Date().toDateString();
          const dataConsulta = new Date(consulta.data_consulta).toDateString();
          return hoje === dataConsulta;
        }).length : 0;

      setDashboardData({
        totalPacientes,
        totalMedicos,
        consultasHoje,
        medicamentos: 0, // Será implementado com a farmácia
        pacientesRecentes: pacientesResponse.data.success ? 
          (pacientesResponse.data.data.pacientes || []).slice(-5) : [],
        consultasRecentes: consultasResponse.data.success ? 
          (consultasResponse.data.data.consultas || []).slice(-5) : []
      });

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const stats = [
    {
      title: 'Pacientes Cadastrados',
      value: dashboardData.totalPacientes.toString(),
      change: '+' + dashboardData.totalPacientes,
      changeType: 'positive',
      icon: FiUsers,
      color: 'terracota'
    },
    {
      title: 'Médicos Ativos',
      value: dashboardData.totalMedicos.toString(),
      change: '+' + dashboardData.totalMedicos,
      changeType: 'positive',
      icon: FiUserCheck,
      color: 'bege'
    },
    {
      title: 'Consultas Hoje',
      value: dashboardData.consultasHoje.toString(),
      change: 'hoje',
      changeType: 'neutral',
      icon: FiCalendar,
      color: 'success'
    },
    {
      title: 'Medicamentos',
      value: dashboardData.medicamentos.toString(),
      change: 'em breve',
      changeType: 'neutral',
      icon: FiPackage,
      color: 'info'
    }
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  // Filtrar consultas de hoje dos dados reais
  const todayAppointments = dashboardData.consultasRecentes
    .filter(consulta => {
      if (!consulta.data_consulta) return false;
      const hoje = new Date().toDateString();
      const dataConsulta = new Date(consulta.data_consulta).toDateString();
      return hoje === dataConsulta;
    })
    .map(consulta => ({
      id: consulta.id,
      time: consulta.horario || '00:00',
      patient: consulta.paciente_nome || 'Paciente não identificado',
      doctor: consulta.medico_nome || 'Médico não identificado',
      type: consulta.tipo_consulta || 'Consulta'
    }))
    .slice(0, 5); // Limitar a 5 consultas

  // Se não há consultas hoje, mostrar mensagem informativa
  const appointmentsToShow = todayAppointments.length > 0 ? todayAppointments : [
    {
      id: 1,
      time: '--:--',
      patient: 'Nenhuma consulta agendada para hoje',
      doctor: 'Agenda livre',
      type: 'Info'
    }
  ];

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>
          Bem-vindo ao sistema de gestão Lumi One Clínica
        </p>
      </div>

      {/* Cards de estatísticas */}
      <div className={styles.statsGrid}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`${styles.statCard} ${styles[stat.color]}`}>
              <div className={styles.statHeader}>
                <div className={styles.statIcon}>
                  <Icon />
                </div>
                <div className={`${styles.statChange} ${styles[stat.changeType]}`}>
                  {stat.change}
                  <FiTrendingUp />
                </div>
              </div>
              <div className={styles.statContent}>
                <h3 className={styles.statValue}>{stat.value}</h3>
                <p className={styles.statTitle}>{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.mainContent}>
        {/* Consultas de hoje */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <FiCalendar />
              Consultas de Hoje
            </h2>
          </div>
          <div className={styles.appointmentsList}>
            {appointmentsToShow.map((appointment) => (
              <div key={appointment.id} className={styles.appointmentItem}>
                <div className={styles.appointmentTime}>
                  <FiClock />
                  {appointment.time}
                </div>
                <div className={styles.appointmentDetails}>
                  <div className={styles.appointmentPatient}>
                    {appointment.patient}
                  </div>
                  <div className={styles.appointmentDoctor}>
                    {appointment.doctor}
                  </div>
                </div>
                <div className={styles.appointmentType}>
                  {appointment.type}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Atividades recentes */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <FiActivity />
              Atividades Recentes
            </h2>
          </div>
          <div className={styles.activitiesList}>
            {dashboardData.pacientesRecentes.length === 0 && dashboardData.consultasRecentes.length === 0 ? (
              <div className={styles.noActivities}>
                <FiActivity />
                <p>Nenhuma atividade recente</p>
                <small>As atividades aparecerão aqui conforme você usar o sistema</small>
              </div>
            ) : (
              <>
                {dashboardData.pacientesRecentes.slice(0, 3).map((paciente, index) => (
                  <div key={`paciente-${paciente.id}`} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <FiUsers />
                    </div>
                    <div className={styles.activityDetails}>
                      <div className={styles.activityTitle}>
                        Paciente cadastrado: {paciente.nome_completo}
                      </div>
                      <div className={styles.activityTime}>
                        {paciente.created_at ? 
                          new Date(paciente.created_at).toLocaleDateString('pt-BR') : 
                          'Recente'
                        }
                      </div>
                    </div>
                  </div>
                ))}
                {dashboardData.consultasRecentes.slice(0, 2).map((consulta, index) => (
                  <div key={`consulta-${consulta.id}`} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <FiCalendar />
                    </div>
                    <div className={styles.activityDetails}>
                      <div className={styles.activityTitle}>
                        Consulta agendada #{consulta.id}
                      </div>
                      <div className={styles.activityTime}>
                        {consulta.data_consulta ? 
                          new Date(consulta.data_consulta).toLocaleDateString('pt-BR') : 
                          'Data não informada'
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;